import React, { useMemo } from "react";
import classnames from "classnames";

import { getLorryDetailsByType } from "@vahak/core/dist/constants";
import { getAllowedVehicleList, NEW_LORRY_CAPACITY } from "@vahak/core/dist/constants/lorryTypes";

import { getLorryCapacityUnitLabel } from "../../../../../methods/getLorryCapacityUnitLabel";
import useHomepageV2Translation from "../../../hooks/useHomepageV2Translation";
import { parseLoadQuoteCapacity } from "../../../load-quote";
import { QUOTE_FORM_LCV_LORRY_TYPE } from "../loadQuoteForm.constants";
import Styles from "./QuoteFormLcvCapacitySlider.module.scss";

function getLcvSliderCapacityBounds() {
    const configuredBounds = NEW_LORRY_CAPACITY.LOAD_POST;

    if (configuredBounds.max > 0) {
        return configuredBounds;
    }

    const lorries = getAllowedVehicleList("load");
    if (!lorries.length) {
        return configuredBounds;
    }

    return lorries.reduce(
        (range, lorry) => ({
            min: Math.min(range.min, lorry.capacity.min),
            max: Math.max(range.max, lorry.capacity.max)
        }),
        { min: Infinity, max: -Infinity }
    );
}

/** Load-post capacity bounds for the hero LCV slider (`NEW_LORRY_CAPACITY.LOAD_POST`). */
export function getQuoteFormLcvSliderCapacityRange() {
    const { min, max } = getLcvSliderCapacityBounds();
    const unit = getLorryCapacityUnitLabel();

    return {
        min,
        max,
        capacityText: `${min}-${max} ${unit}`,
        unit
    };
}

export function getQuoteFormLcvSliderDefaultTonnes() {
    return String(getQuoteFormLcvSliderCapacityRange().min);
}

type QuoteFormLcvCapacitySliderProps = {
    id: string;
    value?: string;
    disabled?: boolean;
    error?: string;
    onChange: (tonnes: string) => void;
};

function getStepDecimalPlaces(step: number) {
    const stepText = String(step);

    if (!stepText.includes(".")) {
        return 0;
    }

    return stepText.split(".")[1]?.length ?? 0;
}

function getSliderStep(min: number, max: number) {
    const range = max - min;

    for (const step of [0.1, 0.25, 0.5, 1]) {
        const stepCount = range / step;

        if (Math.abs(stepCount - Math.round(stepCount)) < 1e-6) {
            return step;
        }
    }

    return 0.1;
}

function getSliderFillPercent(value: number, min: number, max: number) {
    if (max <= min) {
        return 0;
    }

    if (value >= max) {
        return 100;
    }

    return ((value - min) / (max - min)) * 100;
}

function snapSliderValue(value: number, min: number, max: number, step: number) {
    if (value >= max - step / 2) {
        return max;
    }

    const clamped = Math.max(min, Math.min(value, max));
    const snapped = min + Math.round((clamped - min) / step) * step;
    const decimals = getStepDecimalPlaces(step);
    const normalized = Number(Math.min(max, Math.max(min, snapped)).toFixed(decimals));

    return Math.abs(normalized - max) < step / 2 ? max : normalized;
}

function formatCapacityAmount(value: number, step: number) {
    const decimals = getStepDecimalPlaces(step);

    if (decimals === 0) {
        return String(Math.round(value));
    }

    return value.toFixed(decimals);
}

const QuoteFormLcvCapacitySlider = ({
    id,
    value,
    disabled = false,
    error,
    onChange
}: QuoteFormLcvCapacitySliderProps) => {
    const { t } = useHomepageV2Translation();
    const { min, max } = getQuoteFormLcvSliderCapacityRange();
    const unitShort = getLorryDetailsByType(QUOTE_FORM_LCV_LORRY_TYPE)?.unitShort ?? "T";
    const step = getSliderStep(min, max);
    const parsedValue = parseLoadQuoteCapacity(value);
    const sliderValue = snapSliderValue(parsedValue ?? min, min, max, step);
    const sliderProgress = getSliderFillPercent(sliderValue, min, max);

    const capacityUnit = sliderValue === 1 ? t("mktCardV2:tonne") : t("mktCardV2:tonnes");

    const displayValue = useMemo(
        () =>
            t("homepageV2:hero.quoteForm.lcvCapacityValue", {
                value: formatCapacityAmount(sliderValue, step),
                unit: capacityUnit
            }),
        [capacityUnit, sliderValue, step, t]
    );

    const minBoundLabel = formatCapacityAmount(min, step);
    const maxBoundLabel = formatCapacityAmount(max, step);

    const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = snapSliderValue(Number(event.target.value), min, max, step);
        onChange(String(nextValue));
    };

    return (
        <div className={classnames(Styles.section, disabled && Styles.sectionDisabled)}>
            <div className={Styles.valueRow}>
                <span className={Styles.valueLabel}>{t("homepageV2:hero.quoteForm.lcvCapacity")}</span>
                <span className={Styles.value}>{displayValue}</span>
            </div>

            <div className={Styles.sliderBlock}>
                <div className={Styles.sliderWrap}>
                    <input
                        id={id}
                        type="range"
                        className={Styles.slider}
                        style={{ ["--slider-progress" as string]: `${sliderProgress}%` }}
                        min={min}
                        max={max}
                        step={step}
                        value={sliderValue}
                        disabled={disabled || max <= min}
                        data-at-max={sliderValue >= max ? "true" : "false"}
                        onChange={handleSliderChange}
                        onInput={handleSliderChange}
                        aria-label={t("homepageV2:hero.quoteForm.lcvCapacity")}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={sliderValue}
                    />
                </div>

                <div className={Styles.sliderBounds} aria-hidden>
                    <span>
                        {minBoundLabel}
                        <span className={Styles.boundUnit}>{unitShort}</span>
                    </span>
                    <span>
                        {maxBoundLabel}
                        <span className={Styles.boundUnit}>{unitShort}</span>
                    </span>
                </div>
            </div>

            {error ? <span className={Styles.fieldError}>{error}</span> : null}
        </div>
    );
};

export default QuoteFormLcvCapacitySlider;
